import React from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Mail, Copy, Check } from "lucide-react";

export default function InviteCreatorDialog({ open, onClose, onCreated }) {
    const [email, setEmail] = React.useState("");
    const [note, setNote] = React.useState("");
    const [submitting, setSubmitting] = React.useState(false);
    const [result, setResult] = React.useState(null);
    const [copied, setCopied] = React.useState(false);

    React.useEffect(() => {
        if (!open) {
            setEmail("");
            setNote("");
            setResult(null);
            setCopied(false);
        }
    }, [open]);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post("/invites", { email: email.trim().toLowerCase(), note });
            setResult(data);
            toast.success("Invite link generated");
            onCreated?.(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const copy = () => {
        if (!result) return;
        navigator.clipboard.writeText(result.invite_url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="bg-white border-slate-200 rounded-2xl max-w-lg" data-testid="invite-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl flex items-center gap-2">
                        <Mail className="w-5 h-5 text-coral" /> Invite a creator
                    </DialogTitle>
                    <DialogDescription>
                        Send a creator you already work with a link to join your Castline workspace
                        and upload their videos.
                    </DialogDescription>
                </DialogHeader>
                {result ? (
                    <div className="space-y-4" data-testid="invite-result">
                        <div className="surface-warm rounded-xl p-4">
                            <div className="overline text-slate-500 mb-1">INVITE LINK</div>
                            <div className="text-sm break-all text-slate-800 font-mono">
                                {result.invite_url}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={copy}
                                className="btn-pill-primary inline-flex items-center"
                                data-testid="copy-invite"
                            >
                                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                                {copied ? "Copied" : "Copy link"}
                            </button>
                            <button onClick={onClose} className="btn-pill-ghost">
                                Done
                            </button>
                        </div>
                        <div className="text-xs text-slate-400">
                            Email delivery is MOCKED for the MVP — share the link manually.
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Creator email</Label>
                            <Input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="rounded-xl border-slate-200 h-11 focus-visible:ring-coral focus-visible:ring-2"
                                data-testid="invite-email"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Personal note (optional)</Label>
                            <Textarea
                                rows={3}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Hey! Joining Castline so I can run our videos on Meta + Google."
                                className="rounded-xl border-slate-200 focus-visible:ring-coral focus-visible:ring-2"
                                data-testid="invite-note"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-pill-primary w-full"
                            data-testid="invite-submit"
                        >
                            {submitting ? "Generating…" : "Generate invite link"}
                        </button>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
